import { get } from "../../../../utils/api";

export const VENDOR_OTHER_COL = "Vendor (Other specified)";
export const PROJECT_TYPE_OTHER_COL = "Project Type (Other specified)";
export const OTHER_SPECIFIED = "Other specified";
export const OFFSET_MONTH_COL = "Month (in 01/25 or Jan-25)";

export const OFFSET_CREDIT_VENDOR_FETCH_URL = "/master/getAllOffsetCreditVendor";
export const OFFSET_CREDIT_PROJECT_TYPE_FETCH_URL =
  "/master/getAllOffsetCreditProjectType";

export function extractOffsetMasterNames(data = []) {
  return [
    ...new Set(
      data
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.name ?? item?.vendorRegistry ?? item?.projectType ?? "",
        )
        .filter(Boolean),
    ),
  ];
}

export function mergeOffsetMasterOptions(defaultOptions = [], apiValues = []) {
  const defaults = Array.isArray(defaultOptions) ? defaultOptions : [];
  const seen = new Set(defaults);
  const rest = (apiValues || []).filter((value) => value && !seen.has(value));
  return [...defaults, ...rest];
}

export async function fetchOffsetCreditVendorOptions(defaultOptions = []) {
  const response = await get(OFFSET_CREDIT_VENDOR_FETCH_URL);
  return mergeOffsetMasterOptions(
    defaultOptions,
    extractOffsetMasterNames(response?.data?.data || []),
  );
}

export async function fetchOffsetCreditProjectTypeOptions(defaultOptions = []) {
  const response = await get(OFFSET_CREDIT_PROJECT_TYPE_FETCH_URL);
  return mergeOffsetMasterOptions(
    defaultOptions,
    extractOffsetMasterNames(response?.data?.data || []),
  );
}

export const isOtherSpecified = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase() === "other specified";

export const MIN_OFFSET_OTHER_NAME_LENGTH = 2;

const INTERNAL_BULK_ROW_KEYS = new Set(["__excelRowNumber"]);

/** Case-insensitive exact match against a master dropdown list. */
export function isExactMasterOptionMatch(value, options = []) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return false;
  return options.some(
    (opt) => String(opt).trim().toLowerCase() === normalized.toLowerCase(),
  );
}

/** Other-specified name is filled when the custom input has a value. */
export function isOffsetOtherFieldResolved(row, otherColKey) {
  return Boolean(String(row?.[otherColKey] ?? "").trim());
}

/** Field is valid for confirm upload. */
export function isOffsetVendorProjectFieldResolved(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  const entered = String(item?.[enteredKey] ?? row?.[fieldKey] ?? "").trim();
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (
    selected &&
    !isOtherSpecified(selected) &&
    isExactMasterOptionMatch(selected, options)
  ) {
    return true;
  }

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    return isOffsetOtherFieldResolved(row, otherColKey);
  }

  if (selected && !isOtherSpecified(selected)) {
    return isExactMasterOptionMatch(selected, options);
  }

  return isExactMasterOptionMatch(entered, options);
}

/** True when this column shows an input/dropdown that still needs user action. */
export function isOffsetVendorProjectFieldNeedsInput(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  if (isOffsetVendorProjectFieldResolved(item, row, fieldKey, otherColKey, options, enteredKey)) {
    return false;
  }

  const entered = String(item?.[enteredKey] ?? row?.[fieldKey] ?? "").trim();
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    return true;
  }

  return !isExactMasterOptionMatch(entered, options);
}

/** Show Matched chip only when value exactly matches a master option. */
export function isOffsetVendorProjectFieldMatched(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  const entered = String(item?.[enteredKey] ?? "").trim();
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (
    selected &&
    !isOtherSpecified(selected) &&
    isExactMasterOptionMatch(selected, options)
  ) {
    return true;
  }

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    const custom = String(row?.[otherColKey] ?? "").trim();
    const applied = String(row?.[fieldKey] ?? "").trim();
    const valueToMatch = !isOtherSpecified(applied) && applied ? applied : custom;
    if (!valueToMatch) return false;
    return isExactMasterOptionMatch(valueToMatch, options);
  }

  if (selected) return isExactMasterOptionMatch(selected, options);
  return isExactMasterOptionMatch(entered, options);
}

/** Read applied scopes directly from uploaded row data (no dropdown pick). */
export function getOffsetAppliedScopesValue(item, row, title3, keyValue3) {
  return String(item?.[keyValue3] ?? row?.[title3] ?? "").trim();
}

export function isOffsetAppliedScopesResolved(item, row, title3, keyValue3) {
  if (!title3) return true;
  return Boolean(getOffsetAppliedScopesValue(item, row, title3, keyValue3));
}

/** Matched when applied scopes value is present in uploaded data. */
export function isOffsetAppliedScopesMatched(
  item,
  row,
  title3,
  keyValue3,
  options = [],
) {
  const val = getOffsetAppliedScopesValue(item, row, title3, keyValue3);
  if (!val) return false;
  if (!options?.length) return true;
  return isExactMasterOptionMatch(val, options);
}

/** True when the row has a month value (only these rows belong in the review popup). */
export function hasOffsetMonthValue(row = {}) {
  const month = String(row?.[OFFSET_MONTH_COL] ?? "").trim();
  return Boolean(month);
}

/** Keep only rows where Month (in 01/25 or Jan-25) is filled. */
export function filterOffsetRowsWithMonth(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(hasOffsetMonthValue);
}

/** Remove parse-only fields before json upload. */
export function stripOffsetBulkUploadInternalFields(row = {}) {
  if (!row || typeof row !== "object") return row;
  return Object.fromEntries(
    Object.entries(row).filter(([key]) => !INTERNAL_BULK_ROW_KEYS.has(key)),
  );
}

/** Map API detail row number to 0-based array index. */
export function resolveOffsetBulkRowIndex(rows, rowNum) {
  const n = Number(rowNum);
  if (!Number.isFinite(n) || n < 1) return -1;

  const byStoredExcelRow = rows.findIndex(
    (r) => Number(r?.__excelRowNumber) === n,
  );
  if (byStoredExcelRow >= 0) return byStoredExcelRow;

  const bySerial = rows.findIndex((r) => Number(r?.["S No."]) === n);
  if (bySerial >= 0) return bySerial;

  const byExcelRow = n - 2;
  if (byExcelRow >= 0 && byExcelRow < rows.length) return byExcelRow;

  const byOneBased = n - 1;
  if (byOneBased >= 0 && byOneBased < rows.length) return byOneBased;

  return -1;
}

const detailReason = (detail) =>
  String(detail?.reason ?? detail?.error ?? detail?.message ?? "").trim();

/** Normalize ExcelJS / CSV cell values to plain strings. */
export function formatBulkUploadCellValue(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") {
    if (val.text !== undefined) return String(val.text).trim();
    if (Array.isArray(val.richText)) {
      return val.richText.map((part) => part?.text || "").join("").trim();
    }
    if (val.result !== undefined && val.result !== null) {
      return formatBulkUploadCellValue(val.result);
    }
    if (val instanceof Date) {
      const month = String(val.getMonth() + 1).padStart(2, "0");
      const year = String(val.getFullYear()).slice(-2);
      return `${month}/${year}`;
    }
  }
  return String(val).trim();
}

function parseCsvLine(line) {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current);
  return values.map((value) => value.trim());
}

async function parseUploadedCsvFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((header) => header.trim());
  const rows = [];

  for (let i = 1; i < lines.length; i += 1) {
    const values = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((header, index) => {
      if (!header) return;
      row[header] = formatBulkUploadCellValue(values[index] ?? "");
    });
    if (Object.keys(row).length > 0) {
      row.__excelRowNumber = i + 1;
      rows.push(row);
    }
  }

  return filterOffsetRowsWithMonth(rows);
}

/** Pull row objects embedded inside API detail entries. */
function extractRowsFromDetails(details = []) {
  if (!Array.isArray(details)) return [];
  return details
    .map(
      (d) =>
        d?.rowData ||
        d?.data ||
        d?.record ||
        d?.excelRow ||
        d?.rowObject ||
        null,
    )
    .filter((r) => r && typeof r === "object" && !Array.isArray(r));
}

/** Parse uploaded .xlsx / .csv bulk file into row objects. */
export async function parseUploadedExcelFile(file) {
  if (!file) return [];

  const fileName = String(file.name || "").toLowerCase();
  if (fileName.endsWith(".csv")) {
    return parseUploadedCsvFile(file);
  }

  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const sheet =
    workbook.getWorksheet("Template") ||
    workbook.worksheets.find((ws) => ws?.name?.toLowerCase() !== "valid dropdown options") ||
    workbook.worksheets[0];
  if (!sheet) return [];

  const headerRow = sheet.getRow(1);
  const headers = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    headers[colNumber - 1] = formatBulkUploadCellValue(cell.value);
  });

  const rows = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const obj = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const key = headers[colNumber - 1];
      if (!key) return;
      obj[key] = formatBulkUploadCellValue(cell.value);
    });
    if (Object.keys(obj).length > 0) {
      obj.__excelRowNumber = rowNumber;
      rows.push(obj);
    }
  });

  return filterOffsetRowsWithMonth(rows);
}

export const isVendorOtherDetail = (reason) => {
  const r = reason.toLowerCase();
  return (
    r.includes("vendor") &&
    (r.includes("other specified") ||
      r.includes("other vendor") ||
      r.includes("vendor name (if other)"))
  );
};

export const isProjectTypeOtherDetail = (reason) => {
  const r = reason.toLowerCase();
  return (
    r.includes("project type") &&
    (r.includes("other specified") ||
      r.includes("project type (other") ||
      r.includes("project type name"))
  );
};

/** Default match flags from excel row values when statusList is absent. */
export function withOffsetDefaultMatchFlags(row = {}) {
  const vendor = String(row?.Vendor ?? "").trim();
  const projectType = String(row?.["Project Type"] ?? "").trim();
  const vendorOther = String(row?.[VENDOR_OTHER_COL] ?? "").trim();
  const projectTypeOther = String(
    row?.[PROJECT_TYPE_OTHER_COL] ?? "",
  ).trim();

  const vendorOk =
    row.matchedVendor === true ||
    (Boolean(vendor) && !isOtherSpecified(vendor)) ||
    (isOtherSpecified(vendor) && Boolean(vendorOther));

  const projectTypeOk =
    row.matchedProjectType === true ||
    (Boolean(projectType) && !isOtherSpecified(projectType)) ||
    (isOtherSpecified(projectType) && Boolean(projectTypeOther));

  return {
    ...row,
    matchedVendor: vendorOk,
    matchedProjectType: projectTypeOk,
    matchedAppliedScopes: row.matchedAppliedScopes === true,
  };
}

/** Apply API `details: [{ row, reason }]` to review rows + field errors. */
export function applyOffsetBulkUploadDetails(rows = [], details = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { rows: [], fieldErrors: {}, autoShowInput: {} };
  }

  const nextRows = rows.map((row) => withOffsetDefaultMatchFlags({ ...row }));
  const fieldErrors = {};
  const autoShowInput = {};

  if (!Array.isArray(details) || details.length === 0) {
    return { rows: nextRows, fieldErrors, autoShowInput };
  }

  details.forEach((detail) => {
    const idx = resolveOffsetBulkRowIndex(nextRows, detail?.row);
    if (idx < 0) return;

    const reason = detailReason(detail);
    if (!reason) return;

    if (isVendorOtherDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        Vendor: isOtherSpecified(nextRows[idx]?.Vendor)
          ? nextRows[idx].Vendor
          : OTHER_SPECIFIED,
        matchedVendor: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        [VENDOR_OTHER_COL]: reason,
      };
      autoShowInput[`${idx}-Vendor`] = true;
    }

    if (isProjectTypeOtherDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        "Project Type": isOtherSpecified(nextRows[idx]?.["Project Type"])
          ? nextRows[idx]["Project Type"]
          : OTHER_SPECIFIED,
        matchedProjectType: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        [PROJECT_TYPE_OTHER_COL]: reason,
      };
      autoShowInput[`${idx}-Project Type`] = true;
    }
  });

  return { rows: nextRows, fieldErrors, autoShowInput };
}

/** Unwrap nested offset bulk upload API payloads. */
export function normalizeOffsetBulkUploadResponse(responseData = {}) {
  if (!responseData || typeof responseData !== "object") return {};

  let data = { ...responseData };

  if (
    data.data &&
    typeof data.data === "object" &&
    !Array.isArray(data.data)
  ) {
    data = { ...data, ...data.data };
  }

  if (
    Array.isArray(data.data) &&
    !data.excelData?.length &&
    !data.finalData?.length &&
    !data.statusList?.length
  ) {
    data.excelData = data.data;
    if (!data.statusList?.length) {
      data.statusList = data.data;
    }
  }

  if (
    Array.isArray(data.details) &&
    data.details.length > 0 &&
    !data.hasUnmatched
  ) {
    data.hasUnmatched = true;
  }

  const extraExcelKeys = [
    "uploadedData",
    "parsedData",
    "uploadedRows",
    "bulkData",
    "sheetData",
  ];
  for (const key of extraExcelKeys) {
    if (Array.isArray(data[key]) && !data.excelData?.length) {
      data.excelData = data[key];
      if (!data.statusList?.length) {
        data.statusList = data[key];
      }
      break;
    }
  }

  return data;
}

/** Build review rows from offset bulk upload error/success payload. */
export function buildOffsetBulkUploadReviewRows(
  responseData = {},
  matchedKey,
  fallbackExcelRows = [],
) {
  const normalized = normalizeOffsetBulkUploadResponse(responseData);

  let excelRows = Array.isArray(normalized.excelData)
    ? normalized.excelData
    : Array.isArray(normalized.finalData)
      ? normalized.finalData
      : Array.isArray(normalized.rows)
        ? normalized.rows
        : Array.isArray(normalized.records)
          ? normalized.records
          : [];

  if (!excelRows.length && Array.isArray(fallbackExcelRows) && fallbackExcelRows.length) {
    excelRows = fallbackExcelRows;
  }

  excelRows = filterOffsetRowsWithMonth(excelRows);

  const detailRows = extractRowsFromDetails(normalized.details);
  if (!excelRows.length && detailRows.length) {
    excelRows = filterOffsetRowsWithMonth(detailRows);
  }

  const statusList = Array.isArray(normalized[matchedKey])
    ? normalized[matchedKey]
    : Array.isArray(normalized.statusList)
      ? normalized.statusList
      : [];

  let baseRows = [];
  if (excelRows.length > 0 && statusList.length > 0) {
    baseRows = excelRows.map((row) => {
      const statusIndex =
        Number.isFinite(Number(row?.__excelRowNumber)) &&
        row.__excelRowNumber > 1
          ? row.__excelRowNumber - 2
          : null;
      const status =
        statusIndex !== null && statusIndex >= 0
          ? statusList[statusIndex]
          : null;
      return {
        ...row,
        ...(status && typeof status === "object" ? status : {}),
      };
    });
  } else if (statusList.length > 0) {
    baseRows = filterOffsetRowsWithMonth(
      statusList.map((row) =>
        typeof row === "object" ? { ...row } : { value: row },
      ),
    );
  } else if (excelRows.length > 0) {
    baseRows = excelRows.map((row) => withOffsetDefaultMatchFlags({ ...row }));
  }

  const { rows, fieldErrors, autoShowInput } = applyOffsetBulkUploadDetails(
    baseRows,
    normalized.details,
  );

  return {
    rows,
    fieldErrors,
    autoShowInput,
    warningMessage: getOffsetBulkUploadErrorMessage(normalized),
    hasReviewData:
      rows.length > 0 ||
      Boolean(normalized.hasUnmatched) ||
      (Array.isArray(normalized.details) && normalized.details.length > 0),
  };
}

export function getOffsetBulkUploadErrorMessage(responseData = {}) {
  if (responseData?.error) return String(responseData.error);
  if (responseData?.message) return String(responseData.message);
  const fromDetails = (responseData?.details || [])
    .map((d) => detailReason(d))
    .filter(Boolean)
    .join(" ");
  return fromDetails || "Please review and resolve bulk upload data.";
}
